import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Button, Row, Col, Select } from "antd";
import CompanyService from "../../services/CompanyService";
import "./Company.scss";
import { openNotification, handlePhonePaste, toTurkishUpper } from "../../utils";
import AuthorizedPersonService from "../../services/AuthorizedPersonService";
import { filterNotificationTypeOption, notificationTypeOptions } from "../../constants/notificationTypes";

interface AuthorizedPersonDrawerProps {
  visible: boolean;
  onClose: () => void;
  companyId: string;
  existingData: any;
  editPerson?: any;
}

const AuthorizedPersonDrawer: React.FC<AuthorizedPersonDrawerProps> = ({
  visible,
  onClose,
  companyId,
  existingData,
  editPerson,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [controlAddressOptions, setControlAddressOptions] = useState<{ label: string; value: number }[]>([]);

  const firstName = Form.useWatch("firstName", form);
  const lastName = Form.useWatch("lastName", form);
  const email = Form.useWatch("email", form);
  const phoneNumber = Form.useWatch("phoneNumber", form);
  const notificationTypes = Form.useWatch("notificationTypes", form) || [];
  const controlAddressIds = Form.useWatch("controlAddressIds", form) || [];

  const canSave = Boolean(
    String(firstName || "").trim() &&
      String(lastName || "").trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim()) &&
      /^\d{10}$/.test(String(phoneNumber || "").trim()) &&
      Array.isArray(notificationTypes) &&
      notificationTypes.length > 0 &&
      Array.isArray(controlAddressIds) &&
      controlAddressIds.length > 0 &&
      !isLoading
  );

  useEffect(() => {
    const fetchCompanyControlAddresses = async () => {
      if (!visible || !companyId) {
        return;
      }

      try {
        const response = await CompanyService.getCompany(companyId);
        const addresses = Array.isArray(response?.data?.addresses) ? response.data.addresses : [];
        const options = addresses
          .filter((address: any) => address?.type === "CONTROL")
          .map((address: any) => {
            const title = String(address?.title || "").trim();
            const city = String(address?.city || "").trim().toLocaleUpperCase("tr-TR");
            const state = String(address?.state || "").trim().toLocaleUpperCase("tr-TR");
            const location = [city, state].filter(Boolean).join(" / ");

            return {
              value: address.id,
              label: [title || "-", location].filter(Boolean).join(" - "),
            };
          });

        setControlAddressOptions(options);
      } catch (error) {
        console.error("İlgili lokasyonlar yüklenemedi:", error);
        setControlAddressOptions([]);
      }
    };

    fetchCompanyControlAddresses();
  }, [companyId, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (editPerson) {
      form.setFieldsValue({
        firstName: editPerson.firstName,
        lastName: editPerson.lastName,
        position: editPerson.position,
        email: editPerson.email,
        phoneNumber: editPerson.phoneNumber,
        notificationTypes: editPerson.notificationTypes,
        controlAddressIds: Array.isArray(editPerson.controlAddressIds) ? editPerson.controlAddressIds : [],
      });
      return;
    }

    form.resetFields();
    form.setFieldsValue({
      notificationTypes: [],
      controlAddressIds: [],
    });
  }, [editPerson, form, visible]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const input = e.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const upperValue = toTurkishUpper(input.value) ?? input.value;

    form.setFieldsValue({ [fieldName]: upperValue });

    setTimeout(() => {
      input.setSelectionRange(start, end);
    }, 0);
  };

  const onFinish = async () => {
    try {
      setIsLoading(true);
      const values = await form.validateFields();
      const normalizedValues = {
        ...values,
        firstName: toTurkishUpper(values.firstName),
        lastName: toTurkishUpper(values.lastName),
        position: toTurkishUpper(values.position),
      };

      const newPerson = {
        firstName: normalizedValues.firstName || form.getFieldValue("firstName"),
        lastName: normalizedValues.lastName || form.getFieldValue("lastName"),
        position: normalizedValues.position || form.getFieldValue("position"),
        email: (normalizedValues.email.toLowerCase() || form.getFieldValue("email")).toLowerCase(),
        phoneNumber: normalizedValues.phoneNumber || form.getFieldValue("phoneNumber"),
        notificationTypes: normalizedValues.notificationTypes || form.getFieldValue("notificationTypes"),
        controlAddressIds: normalizedValues.controlAddressIds || form.getFieldValue("controlAddressIds"),
      };

      if (editPerson) {
        await AuthorizedPersonService.update(editPerson.id, newPerson);
        openNotification("success", "Başarılı", "Yetkili Güncellendi");
      } else {
        await AuthorizedPersonService.create({ ...newPerson, companyId });
        openNotification("success", "Başarılı", "Yeni Yetkili Eklendi");
      }

      setIsLoading(false);
      onClose();
      form.resetFields();
    } catch (error) {
      console.error("Validation Failed:", error);
      openNotification("error", "Hata", "Yetkili bilgisi güncellenemedi");
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      title={editPerson ? "Yetkiliyi Düzenle" : "Yeni Yetkili Ekle"}
      width={720}
      onClose={onClose}
      visible={visible}
      bodyStyle={{ paddingBottom: 80 }}
      footer={
        <div className="text-right">
          <Button onClick={onFinish} type="primary" loading={isLoading} disabled={!canSave} className="d-button dark-button width-auto">
            Kaydet
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="firstName"
              label="Ad"
              className="form-item"
              rules={[{ required: true, message: "Ad boş bırakılamaz" }]}
            >
              <Input
                placeholder="Ad"
                className="d-input d-input-form"
                lang="tr"
                style={{ textTransform: "uppercase" }}
                onChange={(e) => handleInputChange(e, "firstName")}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lastName"
              label="Soyad"
              className="form-item"
              rules={[{ required: true, message: "Soyad boş bırakılamaz" }]}
            >
              <Input
                placeholder="Soyad"
                className="d-input d-input-form"
                lang="tr"
                style={{ textTransform: "uppercase" }}
                onChange={(e) => handleInputChange(e, "lastName")}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="position" label="Unvan" className="form-item" rules={[]}>
          <Input
            placeholder="Unvan"
            className="d-input d-input-form"
            lang="tr"
            style={{ textTransform: "uppercase" }}
            onChange={(e) => handleInputChange(e, "position")}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="email"
              label="E-Posta"
              className="form-item"
              rules={[{ required: true, type: "email", message: "Geçerli bir e-posta adresi girin" }]}
            >
              <Input placeholder="E-Posta" className="d-input d-input-form " />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phoneNumber"
              label="Telefon"
              className="form-item"
              rules={[
                { required: true, message: "Telefon zorunludur" },
                { pattern: /^\d{10}$/, message: "Telefon 10 haneli olmalı" },
              ]}
            >
              <Input
                placeholder="Telefon"
                className="d-input d-input-form"
                addonBefore="+90"
                maxLength={10}
                onPaste={(e) => handlePhonePaste(e, { form, fieldName: "phoneNumber" })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={24}>
            <Form.Item
              name="controlAddressIds"
              label="Adresler"
              rules={[{ required: true, type: "array", min: 1, message: "En az bir ilgili lokasyon seçilmelidir" }]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder="İlgili lokasyon seçin"
                className="d-input d-input-form"
                options={controlAddressOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={24}>
            <Form.Item
              name="notificationTypes"
              label="İletişim Konuları"
              rules={[{ required: true, type: "array", min: 1, message: "İletişim Konuları zorunludur" }]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder="Yetki türü seçin"
                className="d-input d-input-form"
                options={notificationTypeOptions}
                optionFilterProp="label"
                filterOption={filterNotificationTypeOption}
              />
            </Form.Item>
          </Col>
        
        </Row>
      </Form>
    </Drawer>
  );
};

export default AuthorizedPersonDrawer;
